import { FC, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { PageSpinner } from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useTitle } from 'src/hooks/use-title';
import { useTsoSecondarySettings } from 'src/pages/reporting/secondary-tradings/trade-proposal/tso-secondary-settings-utils';
import { useBalancingCodes } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-balancing-codes';
import { useChannelConfig } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-channel-config';
import { useRemitInfo } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-remit-info';
import { useSecondaryConfig } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-secondary-config';
import {
  TradeProposalRequest,
  useTradeProposal,
} from 'src/pages/reporting/secondary-tradings/trade-proposal/use-trade-proposal';
import { useTraderLists } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-trader-lists';

const Page = lazy(() => import('./page'));

export const ReportingSecondaryTradeProposal: FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  useTitle(`Proposal ID ${proposalId}`);
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const tradeProposal = useTradeProposal({ proposalId });

  if (!tradeProposal.response) return <PageSpinner />;

  if (monolithUser?.isShipper) {
    return <LoadShipperResources tradeProposal={tradeProposal} />;
  } else {
    return <Page tradeProposal={tradeProposal} />;
  }
};

const LoadShipperResources: FC<{
  tradeProposal: Successful<TradeProposalRequest>;
}> = ({ tradeProposal }) => {
  const proposal = tradeProposal.response.data;

  const secondaryConfig = useSecondaryConfig({
    networkPointId: proposal.networkPoint.id,
  });
  const balancingCodes = useBalancingCodes({ proposal });
  const traderLists = useTraderLists();
  const channelConfig = useChannelConfig();
  const remitInfo = useRemitInfo();

  const tso0SecondarySettings = useTsoSecondarySettings({
    tsoId: proposal.networkPoint.tso0Id,
  });
  const tso1SecondarySettings = useTsoSecondarySettings({
    tsoId: proposal.networkPoint.tso1Id,
  });

  if (
    !secondaryConfig.response ||
    !channelConfig.response ||
    !balancingCodes.response ||
    !remitInfo.response ||
    !traderLists.response ||
    (tso0SecondarySettings && !tso0SecondarySettings.loaded) ||
    (tso1SecondarySettings && !tso1SecondarySettings.loaded)
  )
    return <PageSpinner />;

  return (
    <Page
      tradeProposal={tradeProposal}
      secondaryConfig={secondaryConfig.response.data}
      balancingCodes={balancingCodes.response.data}
      traderLists={traderLists.response.data}
      channelConfig={channelConfig.response.data}
      remitInfo={remitInfo.response.data}
      tso0SecondarySettings={tso0SecondarySettings?.response?.data}
      tso1SecondarySettings={tso1SecondarySettings?.response?.data}
    />
  );
};
